import moment from "moment";

export const NO_PREFERENCE_PROVIDER_ID = "no_preference";
export const NO_PREFERENCE_PROVIDER_NAME = "No Preference";

export function isNoPreferenceProvider(providerId) {
  return providerId === NO_PREFERENCE_PROVIDER_ID;
}

export function lowestProviderPrice(providers) {
  if (!Array.isArray(providers) || providers.length === 0) return 0;
  const prices = providers.map((p) => Number(p.price)).filter((n) => Number.isFinite(n));
  return prices.length ? Math.min(...prices) : 0;
}

export function formatMoney(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function serviceRequiresOnlineDeposit(svcOrFlags) {
  if (!svcOrFlags) return false;
  const flag = svcOrFlags.depositBookedOnline ?? svcOrFlags.deposit_booked_online;
  return Number(flag) === 1 || flag === true;
}

function serviceDepositPrice(svcOrFlags) {
  if (!svcOrFlags) return 0;
  const price = svcOrFlags.depositBookedPrice ?? svcOrFlags.deposit_booked_price;
  return Number(price) || 0;
}

/**
 * Sum deposit for lines when service has deposit_booked_online.
 * Prefers deposit fields copied onto booking lines; falls back to catalog cache.
 */
export function computeDepositTotal(bookingLines, serviceByIdFromCache) {
  let total = 0;
  for (const row of bookingLines || []) {
    if (row.depositBookedOnline != null || row.depositBookedPrice != null) {
      if (serviceRequiresOnlineDeposit(row)) {
        total += serviceDepositPrice(row);
      }
      continue;
    }
    const svc = typeof serviceByIdFromCache === "function" ? serviceByIdFromCache(row.serviceId) : null;
    if (serviceRequiresOnlineDeposit(svc)) {
      total += serviceDepositPrice(svc);
    }
  }
  return Math.round(total * 100) / 100;
}

export function buildBookingApiLines(bookingLines) {
  return bookingLines.map((row, index, arr) => {
    const line = {
      service_id: row.serviceId,
      price: Number(row.price) || 0,
      duration_minutes: Math.max(0, Math.round(Number(row.durationMinutes) || 0)),
      padding_minutes: index === arr.length - 1 ? Math.max(0, Math.round(Number(row.paddingMinutes) || 0)) : 0,
    };

    if (row.noPreference) {
      line.no_preference = true;
    } else {
      line.staff_id = row.providerId;
    }

    return line;
  });
}

/** Salon-local moment from portal appointment row (prefers API ISO timestamp). */
export function parseAppointmentOccurrenceMoment(appointment) {
  const iso = appointment?.occurrence_start_at;
  if (iso) {
    const fromIso = moment.parseZone(iso);
    if (fromIso.isValid()) return fromIso;
  }

  const dateStr = appointment?.date;
  const timePart = String(appointment?.time_range || "").split("-")[0]?.trim();
  if (!dateStr || !timePart) return null;

  const tryFormats = ["DD/MM/YYYY", "MM/DD/YYYY", "D/M/YYYY", "M/D/YYYY"];
  for (const f of tryFormats) {
    const d = moment(dateStr, f, true);
    if (d.isValid()) {
      const parsed = moment(`${d.format("YYYY-MM-DD")} ${timePart}`, "YYYY-MM-DD h:mm A", true);
      if (parsed.isValid()) return parsed;
    }
  }

  return null;
}

/**
 * Salon-local start datetime for API: YYYY-MM-DD HH:mm:ss
 */
export function buildStartDatetime({ timeChoice, appointment, calendarMonth, pickedDay, pickedDateStr, pickedSlot }) {
  if (timeChoice === "new") {
    if (!pickedSlot) return null;
    const dateKey =
      pickedDateStr ||
      (calendarMonth && pickedDay ? calendarMonth.clone().date(pickedDay).format("YYYY-MM-DD") : null);
    if (!dateKey) return null;
    const parsed = moment(`${dateKey} ${pickedSlot}`, "YYYY-MM-DD h:mm A", true);
    if (!parsed.isValid()) return null;
    return parsed.format("YYYY-MM-DD HH:mm:ss");
  }

  const occurrence = parseAppointmentOccurrenceMoment(appointment);
  if (!occurrence?.isValid()) return null;
  return occurrence.format("YYYY-MM-DD HH:mm:ss");
}

export function resolveBookingStartMoment({ timeChoice, appointment, calendarMonth, pickedDay, pickedDateStr, pickedSlot }) {
  const dt = buildStartDatetime({ timeChoice, appointment, calendarMonth, pickedDay, pickedDateStr, pickedSlot });
  if (!dt) return null;
  const m = moment(dt, "YYYY-MM-DD HH:mm:ss", true);
  return m.isValid() ? m : null;
}

export function formatWhenFromMoment(m) {
  if (!m?.isValid?.()) return "";
  return `on ${m.format("dddd D MMMM YYYY")} at ${m.format("h:mm A")}`;
}

export function formatDurationHuman(totalMinutes) {
  const m = Math.max(0, Math.round(Number(totalMinutes) || 0));
  if (m <= 0) return "";
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem} min`;
  if (rem === 0) return `${h} hr`;
  return `${h} hr ${rem} min`;
}

/** Per-line estimated time shown in the portal UI (service duration only; padding is not displayed). */
export function serviceLineEstimatedLabel(line) {
  const dur = Math.max(0, Math.round(Number(line?.durationMinutes) || 0));
  if (dur <= 0) return "";
  return formatDurationHuman(dur);
}

export function computeBookingTotalMinutes(linesOrdered) {
  if (!Array.isArray(linesOrdered) || linesOrdered.length === 0) return 0;
  let durSum = 0;
  for (const row of linesOrdered) {
    durSum += Math.max(0, Math.round(Number(row.durationMinutes) || 0));
  }
  const last = linesOrdered[linesOrdered.length - 1];
  const pad = Math.max(0, Math.round(Number(last.paddingMinutes) || 0));
  return durSum + pad;
}

/** Service duration total for portal display labels (excludes padding). */
export function computeBookingDisplayMinutes(linesOrdered) {
  if (!Array.isArray(linesOrdered) || linesOrdered.length === 0) return 0;
  return linesOrdered.reduce(
    (sum, row) => sum + Math.max(0, Math.round(Number(row.durationMinutes) || 0)),
    0
  );
}

/**
 * Labels for each booked service: first service gets the appointment start; later services follow sequentially.
 *
 * @param {Array} bookingLines
 * @param {import('moment').Moment|null} startMoment
 * @param {string} [fallbackWhen] e.g. "on Tuesday 19 May 2026 at 10:00 AM"
 */
export function buildPerServiceSchedule(bookingLines, startMoment, fallbackWhen = "") {
  if (!Array.isArray(bookingLines) || bookingLines.length === 0) return [];

  const n = bookingLines.length;
  const fallback = String(fallbackWhen || "").trim();

  const withDuration = (row, index, extra = {}) => ({
    ...row,
    estimatedDurationLabel: serviceLineEstimatedLabel(row),
    ...extra,
  });

  if (!startMoment?.isValid?.()) {
    return bookingLines.map((row, index) => {
      if (index === 0 && fallback) {
        return withDuration(row, index, {
          scheduleKind: "start",
          scheduleLabel: /^on\s/i.test(fallback) ? fallback : `on ${fallback}`,
        });
      }
      if (index === 0) {
        return withDuration(row, index, { scheduleKind: null, scheduleLabel: null });
      }
      const prevName = bookingLines[index - 1]?.serviceName || "previous service";
      return withDuration(row, index, {
        scheduleKind: "follows",
        scheduleLabel: `After ${prevName} finishes`,
      });
    });
  }

  let offsetMin = 0;
  return bookingLines.map((row, index) => {
    const dur = Math.max(0, Math.round(Number(row.durationMinutes) || 0));
    if (index === 0) {
      const label = formatWhenFromMoment(startMoment);
      offsetMin += dur;
      return withDuration(row, index, { scheduleKind: "start", scheduleLabel: label });
    }
    const segStart = startMoment.clone().add(offsetMin, "minutes");
    const prevName = bookingLines[index - 1]?.serviceName || "previous service";
    const approxTime = segStart.format("h:mm A");
    offsetMin += dur;
    return withDuration(row, index, {
      scheduleKind: "follows",
      scheduleLabel: `After ${prevName} finishes · approx. ${approxTime}`,
    });
  });
}

export function validateBeforeSave({ bookingLines, timeChoice, pickedSlot, isBookMode, startDatetime }) {
  if (!bookingLines.length) return "Select at least one service.";
  if (isBookMode || timeChoice === "new") {
    if (!pickedSlot) return "Choose a time for your appointment.";
    if (!startDatetime) return "Could not determine appointment time.";
  } else if (!startDatetime) {
    return "Could not read your current appointment time.";
  }
  return null;
}
